import React, {useEffect, useRef, useState} from 'react';
import styles from './style.css';
import {rmsSeries, shapeLevel, windowAt} from '../live-waveform/level';
import {BARS, BAR_SECONDS, drawBars, setupCanvas} from '../live-waveform/draw';

// Playback waves that look exactly like the live recording view: a short
// window (BARS x 70ms, about 2.5s) of the audio up to the playhead, scrolling
// left as it plays - nothing is drawn ahead of the playhead. The file is
// decoded once into one level per bar; between the player's time updates the
// playhead is advanced by the clock so the scroll stays smooth.
// If the file cannot be fetched/decoded (a remote file without cross-origin
// access), renders `fallback` and calls `onFailed`.
export default function StickersComponentsRecordedWaveform({src, seconds = 0, playing = false, onFailed, fallback = null}) {
    const canvasRef = useRef(null);
    const [series, setSeries] = useState(null);
    const [failed, setFailed] = useState(false);
    const clock = useRef({seconds: 0, at: 0, playing: false});

    useEffect(() => {
        clock.current = {seconds, at: performance.now(), playing};
    }, [seconds, playing]);

    useEffect(() => {
        let cancelled = false;
        setSeries(null);
        setFailed(false);
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!src || !Ctx) {
            if (src) { setFailed(true); if (onFailed) onFailed(); }
            return undefined;
        }
        const ctx = new Ctx();
        fetch(src)
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer(); })
            // callback form: older Safari has no promise-returning decodeAudioData
            .then(buf => new Promise((resolve, reject) => ctx.decodeAudioData(buf, resolve, reject)))
            .then(audio => {
                if (cancelled) return;
                const count = Math.max(1, Math.ceil(audio.duration / BAR_SECONDS));
                setSeries(rmsSeries(audio.getChannelData(0), count).map(shapeLevel));
            })
            .catch(() => { if (!cancelled) { setFailed(true); if (onFailed) onFailed(); } })
            .then(() => { if (ctx.close) ctx.close().catch(() => {}); });
        return () => { cancelled = true; };
    }, [src]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !series) {
            return undefined;
        }
        const surface = setupCanvas(canvas);
        let frame = 0;
        const draw = (now) => {
            const c = clock.current;
            const t = c.playing ? c.seconds + (now - c.at) / 1000 : c.seconds;
            // before playback starts (and after it ends) show the flat line
            const head = c.playing || c.seconds > 0 ? Math.floor(t / BAR_SECONDS) : -1;
            drawBars(surface, windowAt(series, head, BARS));
            frame = requestAnimationFrame(draw);
        };
        frame = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(frame);
    }, [series]);

    if (failed) {
        return fallback;
    }
    return <canvas ref={canvasRef} className={styles.waveform} aria-hidden="true"/>;
}
